// apps/workflow/src/App.js
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';

// ---- Layouts ----
import ClientLayout from './pages/layouts/ClientLayout';
import DesignerLayout from './pages/layouts/DesignerLayout';
import AdminLayout from './pages/layouts/AdminLayout';

// ---- Login ----
import Login from './pages/Login';

// ---- Client Pages ----
import ClientDashboard from './pages/client/Dashboard';
import ClientProjects from './pages/client/Projects';
import ClientProjectDetail from './pages/client/ProjectDetail';
import ClientPayment from './pages/client/Payment';
import ClientRequirements from './pages/client/Requirements';

// ---- Designer / Freelancer / Admin Pages ----
import DesignerDashboard from './pages/designer/Dashboard';
import DesignerTasks from './pages/designer/Tasks';
import DesignerTaskDetail from './pages/designer/TaskDetail';
import DesignerSubmissions from './pages/designer/Submissions';
import DesignerEarnings from './pages/designer/Earnings';
import DesignerProfile from './pages/designer/Profile';

// ---- Freelancer (only status & subscription) ----
import FreelancerOnboardingStatus from './pages/freelancer/OnboardingStatus';
import FreelancerSubscribe from './pages/freelancer/Subscribe';
// 👇 Freelancer's own payment view
import FreelancerPayments from './pages/freelancer/Payments';

// ---- Admin Pages ----
import AdminUsers from './pages/admin/AdminUsers';
import AdminFreelancers from './pages/admin/AdminFreelancers';
// 👇 Admin payment management page
import AdminPayments from './pages/admin/AdminPayments';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000,
    },
  },
});

// Helper: default path by role
const getDefaultPath = (role) => {
  if (['admin', 'super_admin', 'manager', 'designer', 'freelancer', 'applied_freelancer'].includes(role)) {
    return '/designer/dashboard';
  }
  return '/client/dashboard';
};

// Helper: layout by role
const getLayoutByRole = (role) => {
  if (['admin', 'super_admin', 'manager'].includes(role)) return AdminLayout;
  if (['designer', 'freelancer', 'applied_freelancer'].includes(role)) return DesignerLayout;
  return ClientLayout;
};

const AppRoutes = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-slate-500 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  const role = user?.role || 'client';
  const defaultPath = getDefaultPath(role);
  console.log('🔐 Current user role:', role);

  const LayoutComponent = getLayoutByRole(role);

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/" element={<Navigate to={defaultPath} replace />} />

      {/* ---- Client Routes ---- */}
      <Route path="/client" element={<ClientLayout />}>
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<ClientDashboard />} />
        <Route path="projects" element={<ClientProjects />} />
        <Route path="project/:id" element={<ClientProjectDetail />} />
        <Route path="payment" element={<ClientPayment />} />
        <Route path="requirements" element={<ClientRequirements />} />
      </Route>

      {/* ---- Designer / Freelancer / Admin Routes ---- */}
      <Route path="/designer" element={<LayoutComponent />}>
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<DesignerDashboard />} />
        <Route path="tasks" element={<DesignerTasks />} />
        <Route path="task/:id" element={<DesignerTaskDetail />} />
        <Route path="submissions" element={<DesignerSubmissions />} />
        <Route path="earnings" element={<DesignerEarnings />} />
        <Route path="profile" element={<DesignerProfile />} />
        
        {/* 👇 Freelancer's own payments (bi‑weekly earning summary) */}
        <Route path="payments" element={<FreelancerPayments />} />

        {/* 👇 Admin payment management (list all freelancer earnings, mark paid) */}
        <Route path="admin/payments" element={<AdminPayments />} />

        {/* Admin routes */}
        <Route path="admin/users" element={<AdminUsers />} />
        <Route path="admin/freelancers" element={<AdminFreelancers />} />
      </Route>

      {/* ---- Freelancer Status Check (public) ---- */}
      <Route path="/freelancer/status" element={<FreelancerOnboardingStatus />} />

      {/* ---- Freelancer Subscription (for freelancers who are approved but need to pay) ---- */}
      <Route path="/freelancer/subscribe" element={<FreelancerSubscribe />} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
        <Toaster position="top-right" richColors closeButton />
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;